import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
// ADMIN_EMAILS is the one founder list, shared with the auth context (2026-09-27).
import { useAuth, ADMIN_EMAILS } from '@/contexts/SupabaseAuthContext';
import { Button } from '@/components/ui/button';
import SocialLinks from '@/components/SocialLinks';
import { LayoutDashboard, LogOut, Menu, X, CircleUser, Cpu, ChevronDown, Settings } from 'lucide-react';

interface HeaderProps {
  setIsCartOpen?: (open: boolean) => void;
}

const Header = ({ setIsCartOpen: _setIsCartOpen }: HeaderProps) => {
  const { user, signOut, isFounder } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [allPagesMenuOpen, setAllPagesMenuOpen] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const allPagesMenuRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target as Node)) {
        setAdminMenuOpen(false);
      }
      if (allPagesMenuRef.current && !allPagesMenuRef.current.contains(event.target as Node)) {
        setAllPagesMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Real admin signal is isFounder (reads the profile's is_admin via the auth
  // context), so phone accounts with no auth email are recognized. The email list
  // is kept only as a fallback.
  const isAdmin = isFounder || !!(user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase()));

  // Public nav — shared by logged-out and logged-in headers, desktop and mobile.
  const navLinks = [
    { name: 'ROOMS', path: '/rooms' },
    { name: 'THE HULL', path: '/hull' },
    { name: 'FIELD NOTES', path: '/field-notes' },
    { name: 'ABOUT', path: '/maslow' },
  ];

  // Active-state helper — shared by both header branches; reads location (hoisted above).
  const isActive = (path: string) => location.pathname === path;

  // Show simplified header with Sign In button when not logged in
  if (!user) {
    return (
      <header className="sticky top-0 z-50 w-full bg-[color-mix(in_srgb,var(--cream)_90%,transparent)] backdrop-blur-md border-b border-[color-mix(in_srgb,var(--navy)_10%,transparent)] shadow-sm transition-all duration-300">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <span className="text-xl font-serif font-bold text-maslow-navy tracking-widest group-hover:text-maslow-gold transition-colors uppercase">
              Maslow
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-xs tracking-widest transition-colors uppercase ${isActive(link.path) ? 'text-maslow-gold' : 'text-maslow-navy hover:text-maslow-gold'}`}
              >
                {link.name}
              </Link>
            ))}

            <SocialLinks />

            <Link
              to="/login"
              className="px-5 py-2 rounded-full transition-all hover:bg-[color-mix(in_srgb,var(--blue)_6%,transparent)]"
              style={{
                border: '1px solid color-mix(in srgb, var(--blue) 30%, transparent)',
                color: 'var(--navy)',
                fontFamily: 'var(--sans)',
                fontSize: '11px',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
              }}
            >
              Member Access
            </Link>
          </nav>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center gap-4">
            <SocialLinks />
            <Button
              variant="ghost"
              className="text-maslow-navy"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-maslow-cream border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] py-6 absolute w-full shadow-xl left-0 right-0 h-screen z-50">
            <div className="container mx-auto px-4 flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-lg tracking-widest uppercase ${isActive(link.path) ? 'text-maslow-gold' : 'text-maslow-navy'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}

              <Link
                to="/login"
                className="text-lg tracking-widest uppercase text-maslow-navy"
                onClick={() => setMobileMenuOpen(false)}
              >
                Member Access
              </Link>
            </div>
          </div>
        )}
      </header>
    );
  }

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  // Full sitemap for the admin "All pages" review dropdown.
  // Excluded deliberately (need a param or are redirect/alias targets):
  // /product/:id, /locations/:slug, /field-notes/:slug, /checkout-success,
  // /presale (alias), /sanctuary and /suites (redirects).
  const pageGroups = [
    {
      label: 'Story & space',
      links: [
        { name: 'The Hull', path: '/hull' },
        { name: 'Rooms', path: '/rooms' },
        { name: 'Maslow', path: '/maslow' },
      ],
    },
    {
      label: 'Membership & pricing',
      links: [
        { name: 'FAQ', path: '/faq' },
      ],
    },
    {
      label: 'Commerce',
      links: [
        { name: 'Store', path: '/store' },
        { name: 'Buy Credits', path: '/buy-credits' },
        { name: 'Events', path: '/events' },
      ],
    },
    {
      label: 'Account',
      links: [
        { name: 'Profile', path: '/profile' },
        { name: 'Settings', path: '/profile/settings' },
      ],
    },
    {
      label: 'Content',
      links: [
        { name: 'Field Notes', path: '/field-notes' },
      ],
    },
    {
      label: 'Legal & misc',
      links: [
        { name: 'Privacy Policy', path: '/privacy-policy' },
        { name: 'Terms of Service', path: '/terms-of-service' },
        { name: 'Disclaimer', path: '/disclaimer' },
        { name: 'Cookie Policy', path: '/cookie-policy' },
        { name: 'Go', path: '/go' },
      ],
    },
    {
      label: 'Presale',
      links: [
        { name: 'Build', path: '/build' },
      ],
    },
    {
      label: 'Admin',
      adminOnly: true,
      links: [
        { name: 'Admin', path: '/admin' },
        { name: 'Dashboard', path: '/dashboard' },
        { name: 'Field Research', path: '/admin/field-research' },
        { name: 'Research Results', path: '/admin/research-results' },
        { name: 'Prototypes', path: '/prototypes' },
        { name: 'Model', path: '/model' },
      ],
    },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-[color-mix(in_srgb,var(--cream)_90%,transparent)] backdrop-blur-md border-b border-[color-mix(in_srgb,var(--navy)_10%,transparent)] shadow-sm transition-all duration-300">
      <div className="container mx-auto px-4 h-20 flex items-center justify-between w-full">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group shrink-0">
          <span className="text-xl font-serif font-bold text-maslow-navy tracking-widest group-hover:text-maslow-gold transition-colors uppercase">
            Maslow
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`text-xs tracking-widest transition-colors uppercase ${isActive(link.path) ? 'text-maslow-gold' : 'text-maslow-navy hover:text-maslow-gold'}`}
            >
              {link.name}
            </Link>
          ))}

          {/* User Controls */}
          <div className="flex items-center gap-2 border-l border-[color-mix(in_srgb,var(--navy)_20%,transparent)] pl-6">
            <Link to="/profile">
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 text-xs uppercase tracking-wider ${isActive('/profile') ? 'text-maslow-gold' : 'text-maslow-navy hover:text-maslow-gold'}`}
              >
                <CircleUser className="w-4 h-4 mr-2" />
                Profile
              </Button>
            </Link>

            {isAdmin && (
              <div className="relative" ref={adminMenuRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                  className="text-maslow-navy hover:text-maslow-gold hover:bg-[color-mix(in_srgb,var(--navy)_5%,transparent)] h-8 text-xs uppercase tracking-wider"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Admin
                  <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${adminMenuOpen ? 'rotate-180' : ''}`} />
                </Button>
                {adminMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-[color-mix(in_srgb,var(--navy)_10%,transparent)] py-2 z-50">
                    <Link
                      to="/admin"
                      onClick={() => setAdminMenuOpen(false)}
                      className="flex items-center px-4 py-3 text-sm text-maslow-navy hover:bg-[color-mix(in_srgb,var(--navy)_5%,transparent)] hover:text-maslow-gold"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-3" />
                      Dashboard
                    </Link>
                    <Link
                      to="/prototypes"
                      onClick={() => setAdminMenuOpen(false)}
                      className="flex items-center px-4 py-3 text-sm text-maslow-navy hover:bg-[color-mix(in_srgb,var(--navy)_5%,transparent)] hover:text-maslow-gold"
                    >
                      <Cpu className="w-4 h-4 mr-3" />
                      Prototypes
                    </Link>
                  </div>
                )}
              </div>
            )}

            {isAdmin && (
              <div className="relative" ref={allPagesMenuRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAllPagesMenuOpen(!allPagesMenuOpen)}
                  className="text-maslow-navy hover:text-maslow-gold hover:bg-[color-mix(in_srgb,var(--navy)_5%,transparent)] h-8 text-xs uppercase tracking-wider"
                >
                  All pages
                  <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${allPagesMenuOpen ? 'rotate-180' : ''}`} />
                </Button>
                {allPagesMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 max-h-[70vh] overflow-y-auto bg-white rounded-lg shadow-lg border border-[color-mix(in_srgb,var(--navy)_10%,transparent)] py-2 z-50">
                    {pageGroups.map((group) => {
                      if (group.adminOnly && !isAdmin) return null;
                      return (
                        <div key={group.label} className="py-1">
                          <p className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-[color-mix(in_srgb,var(--navy)_40%,transparent)]">
                            {group.label}
                          </p>
                          {group.links.map((link) => (
                            <Link
                              key={link.path}
                              to={link.path}
                              onClick={() => setAllPagesMenuOpen(false)}
                              className={`block px-4 py-2 text-sm hover:bg-[color-mix(in_srgb,var(--navy)_5%,transparent)] hover:text-maslow-gold ${isActive(link.path) ? 'text-maslow-gold' : 'text-maslow-navy'}`}
                            >
                              {link.name}
                            </Link>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[color-mix(in_srgb,var(--navy)_60%,transparent)] hover:text-red-600 hover:bg-red-50 h-8">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <div className="md:hidden flex items-center gap-4">
          <Button
            variant="ghost"
            className="text-maslow-navy"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-maslow-cream border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] py-6 absolute w-full shadow-xl left-0 right-0 h-screen z-50">
          <div className="container mx-auto px-4 flex flex-col gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-lg tracking-widest uppercase ${isActive(link.path) ? 'text-maslow-gold' : 'text-maslow-navy'}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}

            <Link
              to="/profile"
              className={`text-lg tracking-widest uppercase ${isActive('/profile') ? 'text-maslow-gold' : 'text-maslow-navy'}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Profile
            </Link>

            {isAdmin && (
              <Link
                to="/admin"
                className="text-lg tracking-widest uppercase text-maslow-navy"
                onClick={() => setMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
            )}

            {isAdmin && (
              <Link
                to="/prototypes"
                className="text-lg tracking-widest uppercase text-maslow-navy"
                onClick={() => setMobileMenuOpen(false)}
              >
                Prototypes
              </Link>
            )}

            {isAdmin && (
              <div className="flex flex-col gap-4 border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)] pt-6">
                {pageGroups.map((group) => {
                  if (group.adminOnly && !isAdmin) return null;
                  return (
                    <div key={group.label} className="flex flex-col gap-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-[color-mix(in_srgb,var(--navy)_40%,transparent)]">
                        {group.label}
                      </p>
                      {group.links.map((link) => (
                        <Link
                          key={link.path}
                          to={link.path}
                          className={`text-lg tracking-widest uppercase ${isActive(link.path) ? 'text-maslow-gold' : 'text-maslow-navy'}`}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-left text-lg tracking-widest uppercase text-red-600"
            >
              Sign Out
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
